import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import RootErrorBoundary from "@/components/RootErrorBoundary";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { PrefsProvider } from "@/contexts/PrefsContext";
import { LocaleProvider } from "@/contexts/LocaleContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import ModuleHub from "@/pages/ModuleHub";
import OrganiseVaultInbox from "@/pages/organise/VaultInbox";
import VaultJourney from "@/pages/profile/VaultJourney";
import AuditorShares from "@/pages/AuditorShares";
import AuditorView from "@/pages/AuditorView";
import SetupWizard from "@/pages/SetupWizard";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import Cards from "@/pages/Cards";
import CardForm from "@/pages/CardForm";
import Expenses from "@/pages/Expenses";
import QuickEntry from "@/pages/QuickEntry";
import Statements from "@/pages/Statements";
import StatementLedgerPage from "@/pages/StatementLedgerPage";
import Payments from "@/pages/Payments";
import Reconciliation from "@/pages/Reconciliation";
import BankAccounts from "@/pages/BankAccounts";
import Subscriptions from "@/pages/Subscriptions";
import Disputes from "@/pages/Disputes";
import CalendarView from "@/pages/CalendarView";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import AuditTrail from "@/pages/AuditTrail";
import CardHistory from "@/pages/CardHistory";
import CardHolders from "@/pages/CardHolders";
import Budgets from "@/pages/Budgets";
import Offers from "@/pages/Offers";
import Alerts from "@/pages/Alerts";
import AdminSettings from "@/pages/AdminSettings";
import Documents from "@/pages/Documents";
import MonthlyClosing from "@/pages/MonthlyClosing";
import FinanceReview from "@/pages/FinanceReview";
import Home from "@/pages/Home";
import Security from "@/pages/Security";
import StatementReview from "@/pages/StatementReview";
import DataHealth from "@/pages/DataHealth";
import CardDetail from "@/pages/CardDetail";
import Copilot from "@/pages/Copilot";
import Help from "@/pages/Help";
import EmailCapture from "@/pages/EmailCapture";
import CreditScore from "@/pages/CreditScore";
import Assets from "@/pages/Assets";
import AssetDetail from "@/pages/AssetDetail";
import Renewals from "@/pages/Renewals";
import Timeline from "@/pages/Timeline";
import HomeHub from "@/pages/HomeHub";
import Vehicles from "@/pages/Vehicles";
import VehicleDetail from "@/pages/VehicleDetail";
import HealthScore from "@/pages/HealthScore";
import Milestones from "@/pages/Milestones";
import Features from "@/pages/Features";
import Investments from "@/pages/Investments";
import TaxCentre from "@/pages/TaxCentre";
import Family from "@/pages/Family";
import Journeys from "@/pages/Journeys";
import JourneyDetail from "@/pages/JourneyDetail";
import SmartCaptureInbox from "@/pages/SmartCaptureInbox";
import { ForexCards, ForexWallets, ForexExpenses, ForexReloads, Trips } from "@/pages/Forex";
import ForexDashboard from "@/pages/ForexDashboard";
import CommandCentreV2 from "@/pages/CommandCentreV2";
import Travel from "@/pages/Travel";
import AirportMode from "@/pages/AirportMode";
import ProductTravel from "@/pages/public/ProductTravel";
import PeopleAccess from "@/pages/PeopleAccess";
import SuperAdmin from "@/pages/SuperAdmin";
import SuperAdminCommandCentre from "@/pages/SuperAdminCommandCentre";
import SuperAdminCustomerSuccess from "@/pages/SuperAdminCustomerSuccess";
import SuperAdminLaunchCentre from "@/pages/SuperAdminLaunchCentre";
import SuperAdminReleases from "@/pages/SuperAdminReleases";
import SaasOps from "@/pages/SaasOps";
import Pricing from "@/pages/Pricing";
import Contact from "@/pages/Contact";
import CashDesk from "@/pages/CashDesk";
import Signup from "@/pages/Signup";
import InviteAccept from "@/pages/InviteAccept";
import Concierge from "@/pages/Concierge";
import AllTools from "@/pages/AllTools";
import BillingSuccess from "@/pages/BillingSuccess";
import BillingCancel from "@/pages/BillingCancel";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Income from "@/pages/Income";
import CashFlow from "@/pages/CashFlow";
import ExceptionCentre from "@/pages/ExceptionCentre";
import Bills from "@/pages/Bills";
import BillForm from "@/pages/BillForm";
import BillDetail from "@/pages/BillDetail";
import StatementPasswords from "@/pages/StatementPasswords";
import AnnualFeeWaiver from "@/pages/AnnualFeeWaiver";
import CardAdvisor from "@/pages/CardAdvisor";
import Refunds from "@/pages/Refunds";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ChangePassword from "@/pages/ChangePassword";
import AuthCallback from "@/pages/AuthCallback";
import AccessPending from "@/pages/AccessPending";
import BetaProgramme from "@/pages/public/BetaProgramme";
import TestimonialSubmit from "@/pages/public/TestimonialSubmit";
import TestimonialRenewed from "@/pages/public/TestimonialRenewed";
import LegalConfigCentre from "@/pages/LegalConfigCentre";
import Landing from "@/pages/Landing";
import DemoAutoLogin from "@/pages/DemoAutoLogin";
import Trust from "@/pages/Trust";
import Status from "@/pages/Status";
import AIDisclosure from "@/pages/AIDisclosure";
import VaultIntelligence from "@/pages/VaultIntelligence";
import Solutions from "@/pages/Solutions";
import PublicPage from "@/pages/public/PublicPage";
import PricingPublic from "@/pages/public/PricingPublic";
import PricingV2 from "@/pages/public/PricingV2";
import TrustCentre from "@/pages/public/TrustCentre";
import NotFound from "@/pages/NotFound";
import SettingsScreenPrivacy from "@/pages/SettingsScreenPrivacy";
import SettingsScreenDataExport from "@/pages/SettingsScreenDataExport";
import MerchantRulesAdmin from "@/pages/MerchantRulesAdmin";
import VaultInbox from "@/pages/VaultInbox";
import FinancialCalendar from "@/pages/FinancialCalendar";
import ForwardView from "@/pages/ForwardView";
import DataFreshness from "@/pages/DataFreshness";
import Goals from "@/pages/Goals";
import ConnectedServices from "@/pages/ConnectedServices";
import MarketApproval from "@/pages/MarketApproval";
import ShareExperience from "@/pages/ShareExperience";
import AdminTestimonials from "@/pages/AdminTestimonials";
import BrandSocialIdentity from "@/pages/BrandSocialIdentity";
import CreativeQA from "@/pages/CreativeQA";
import CompetitorIntel from "@/pages/CompetitorIntel";
import VaultTogether from "@/pages/VaultTogether";
import HouseholdGoals from "@/pages/household/HouseholdGoals";
import HouseholdTasks from "@/pages/household/HouseholdTasks";
import HouseholdReviews from "@/pages/household/HouseholdReviews";
import CoupleCheckIn from "@/pages/household/CoupleCheckIn";
import FamilyReview from "@/pages/household/FamilyReview";
import HouseholdSummary from "@/pages/household/HouseholdSummary";
import HouseholdOnboarding from "@/pages/household/HouseholdOnboarding";
import HouseholdMoneyMap from "@/pages/household/HouseholdMoneyMap";
import { FeatureStatusProvider } from "@/components/public/StatusPill";
import {
    ProductPage, FeaturePage, TogetherPage, CouplesPage, FamiliesPage,
    TogetherGoalsPage, TogetherPrivacyPage, CalendarPublicPage,
    InboxPublicPage, HealthPublicPage, IntelligencePublicPage,
    AboutPage, ResourcesPage, StoriesPage,
} from "@/pages/public/MarketingPages";
import DemoLanding from "@/pages/public/DemoLanding";
import DemoDashboard from "@/pages/public/DemoDashboard";
import LearnHome from "@/pages/public/LearnHome";
import ArticleReader from "@/pages/public/ArticleReader";
import ToolsHome from "@/pages/public/ToolsHome";
import CheckupTool from "@/pages/public/CheckupTool";
import Dictionary from "@/pages/public/Dictionary";
import LifeMoments from "@/pages/public/LifeMoments";
import Forecast from "@/pages/Forecast";
import ReviewQueue from "@/pages/ReviewQueue";
import { SecurityPublicPage, TrustCentrePublicPage } from "@/pages/public/SecurityAndTrust";
import TrustVault from "@/pages/public/TrustVault";
import SmartPublicOrApp from "@/components/public/SmartPublicOrApp";
import AudienceLanding from "@/pages/public/AudienceLanding";
import LaunchReadiness from "@/pages/admin/LaunchReadiness";
import ClaudeSettings from "@/pages/admin/ClaudeSettings";
import ResourceArticle from "@/pages/public/ResourceArticle";
import BrandFooter from "@/components/BrandFooter";
import ComplianceOps from "@/pages/admin/ComplianceOps";
import ComplianceCockpit from "@/pages/admin/ComplianceCockpit";
import ContactMessagesAdmin from "@/pages/admin/ContactMessagesAdmin";
import PaddleReadiness from "@/pages/admin/PaddleReadiness";
import SupportAccessAdmin from "@/pages/admin/SupportAccessAdmin";
import FeatureHealth from "@/pages/admin/FeatureHealth";
import MarketingCentre from "@/pages/admin/MarketingCentre";
import Onboarding from "@/pages/Onboarding";
import BulkImport from "@/pages/BulkImport";
import DeleteAccount from "@/pages/DeleteAccount";
import CardIntelligence from "@/pages/CardIntelligence";
import ActionCentre from "@/pages/ActionCentre";
import WalletSettings from "@/pages/WalletSettings";
import PasskeysManager from "@/pages/PasskeysManager";
import AuthenticationMetrics from "@/pages/admin/AuthenticationMetrics";
import SystemHealth from "@/pages/admin/SystemHealth";
import CashFlowReport from "@/pages/CashFlowReport";
import ConnectedAccounts from "@/pages/ConnectedAccounts";
import IntegrationsAdmin from "@/pages/admin/IntegrationsAdmin";
import PrivacyDataDashboard from "@/pages/PrivacyDataDashboard";
import CheckoutConfirm from "@/pages/CheckoutConfirm";
import BillingPlan from "@/pages/BillingPlan";
import { SecurePrivacyProvider } from "@/contexts/SecurePrivacyContext";
import CookieConsent from "@/components/public/CookieConsent";
import { Toaster } from "@/components/ui/sonner";
import StepUpModal from "@/components/StepUpModal";
import ScreenshotToast from "@/components/security/ScreenshotToast";

// Extract role arrays so React sees stable references across renders.
const ROLES_ADMIN_FINANCE = ["admin", "finance"];
const ROLES_ADMIN_FINANCE_VIEWER = ["admin", "finance", "viewer"];
const ROLES_ADMIN_FINANCE_ENTRY = ["admin", "finance", "expense_user"];
const ROLES_ADMIN_ONLY = ["admin"];

export default function App() {
    return (
        <RootErrorBoundary>
        <HelmetProvider>
        <FeatureStatusProvider>
        <AuthProvider>
            <PrefsProvider>
            <LocaleProvider>
            <BrowserRouter>
                <ScrollToTop />
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/product" element={<ProductPage />} />
                    <Route path="/product/travel" element={<ProductTravel />} />
                    <Route path="/features/:slug" element={<FeaturePage />} />
                    <Route path="/together" element={<TogetherPage />} />
                    <Route path="/together/couples" element={<CouplesPage />} />
                    <Route path="/together/families" element={<FamiliesPage />} />
                    <Route path="/together/goals" element={<TogetherGoalsPage />} />
                    <Route path="/together/privacy" element={<TogetherPrivacyPage />} />
                    <Route path="/calendar-public" element={<CalendarPublicPage />} />
                    <Route path="/inbox-public" element={<InboxPublicPage />} />
                    <Route path="/health-public" element={<HealthPublicPage />} />
                    {/* PUBLIC entry points for pages that also have an authenticated
                        in-app version. Kept OUTSIDE the ProtectedRoute block so
                        logged-out visitors don't get bounced to /login. Account
                        security controls remain at /settings/security (protected). */}
                    <Route path="/security" element={<SecurityPublicPage />} />
                    <Route path="/health" element={<HealthPublicPage />} />
                    <Route path="/calendar" element={<CalendarPublicPage />} />
                    <Route path="/inbox" element={<InboxPublicPage />} />
                    <Route path="/intelligence" element={<IntelligencePublicPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/resources" element={<ResourcesPage />} />
                    <Route path="/stories" element={<StoriesPage />} />
                    <Route path="/for-individuals" element={<Navigate to="/solutions/individuals" replace />} />
                    <Route path="/for-couples" element={<Navigate to="/solutions/couples" replace />} />
                    <Route path="/for-families" element={<Navigate to="/solutions/families" replace />} />
                    <Route path="/resources/:slug" element={<ResourceArticle />} />
                    <Route path="/solutions/:slug" element={<Solutions />} />
                    <Route path="/pricing" element={<PricingPublic />} />
                    <Route path="/pricing-v2" element={<Navigate to="/pricing" replace />} />
                    <Route path="/features" element={<PublicPage />} />
                    <Route path="/how-it-works" element={<PublicPage />} />
                    {/* /security, /calendar, /inbox, /health are handled by the
                        SmartPublicOrApp splitters inside the auth block below —
                        public visitors see the marketing page, signed-in users see the app. */}
                    <Route path="/privacy-centre" element={<PublicPage />} />
                    <Route path="/demo" element={<DemoLanding />} />
                    <Route path="/demo/:persona" element={<DemoDashboard />} />
                    <Route path="/demo/start" element={<DemoAutoLogin />} />
                    <Route path="/learn" element={<LearnHome />} />
                    <Route path="/learn/articles/:slug" element={<ArticleReader />} />
                    <Route path="/learn/guides/:slug" element={<LearnHome />} />
                    <Route path="/learn/money-minute/:slug" element={<LearnHome />} />
                    <Route path="/learn/dictionary" element={<Dictionary />} />
                    <Route path="/tools" element={<ToolsHome />} />
                    <Route path="/tools/financial-organisation-checkup" element={<CheckupTool />} />
                    <Route path="/life-moments" element={<LifeMoments />} />
                    <Route path="/life-moments/:slug" element={<LifeMoments />} />
                    <Route path="/trust" element={<TrustVault />} />
                    <Route path="/trust-legacy" element={<TrustCentrePublicPage />} />
                    <Route path="/trust-classic" element={<Trust />} />
                    <Route path="/status" element={<Status />} />
                    <Route path="/ai-disclosure" element={<AIDisclosure />} />
                    <Route path="/about" element={<PublicPage />} />
                    <Route path="/contact" element={<PublicPage />} />
                    <Route path="/contact-sales" element={<PublicPage />} />
                    <Route path="/help" element={<PublicPage />} />
                    <Route path="/faq" element={<PublicPage />} />
                    <Route path="/terms" element={<PublicPage />} />
                    <Route path="/privacy" element={<PublicPage />} />
                    <Route path="/cookies" element={<PublicPage />} />
                    <Route path="/billing-terms" element={<PublicPage />} />
                    <Route path="/refund-policy" element={<PublicPage />} />
                    <Route path="/acceptable-use" element={<PublicPage />} />
                    <Route path="/financial-disclaimer" element={<PublicPage />} />
                    <Route path="/accessibility" element={<PublicPage />} />
                    <Route path="/subprocessors" element={<PublicPage />} />
                    <Route path="/status" element={<PublicPage />} />
                    <Route path="/security/report-vulnerability" element={<PublicPage />} />
                    <Route path="/changelog" element={<PublicPage />} />
                    <Route path="/roadmap" element={<PublicPage />} />
                    <Route path="/press" element={<PublicPage />} />
                    <Route path="/press-kit" element={<PublicPage />} />
                    <Route path="/support" element={<PublicPage />} />
                    <Route path="/beta" element={<Navigate to="/signup" replace />} />
                    <Route path="/testimonial-submit" element={<TestimonialSubmit />} />
                    <Route path="/testimonial-renewed" element={<TestimonialRenewed />} />
                    <Route path="/help/share-experience" element={<ShareExperience />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/invite/:token" element={<InviteAccept />} />
                    <Route path="/billing/success" element={<BillingSuccess />} />
                    <Route path="/billing/cancel" element={<BillingCancel />} />
                    <Route path="/auditor/:token" element={<AuditorView />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/access-pending" element={<AccessPending />} />
                    {/* Public 404 catch-all — matched only if no earlier route hits
                        AND the user is not logged in. Authenticated users still
                        get bounced to /dashboard via the ProtectedRoute wrapper. */}
                    <Route path="/404" element={<NotFound />} />
                    <Route path="/change-password" element={
                        <ProtectedRoute allowWhenMustChangePassword>
                            <ChangePassword forced={new URLSearchParams(window.location.search).get("forced") === "1"} />
                        </ProtectedRoute>
                    } />
                    <Route
                        element={
                            <ProtectedRoute>
                                <SecurePrivacyProvider>
                                    <AppLayout />
                                </SecurePrivacyProvider>
                            </ProtectedRoute>
                        }
                    >
                        <Route path="/dashboard" element={<Concierge />} />
                        <Route path="/dashboard-v2" element={<CommandCentreV2 />} />
                        <Route path="/concierge" element={<Concierge />} />
                        <Route path="/dashboard-classic" element={<Home />} />
                        <Route path="/tools" element={<AllTools />} />
                        <Route path="/hub/:slug" element={<ModuleHub />} />
                        <Route path="/finance-review" element={<FinanceReview />} />
                        <Route path="/copilot" element={<Copilot />} />
                        <Route path="/help" element={<Help />} />
                        <Route path="/email-capture" element={<EmailCapture />} />
                        <Route path="/credit-score" element={<CreditScore />} />
                        <Route path="/assets" element={<Assets />} />
                        <Route path="/assets/:id" element={<AssetDetail />} />
                        <Route path="/renewals" element={<Renewals />} />
                        <Route path="/timeline" element={<Timeline />} />
                        <Route path="/home-hub" element={<HomeHub />} />
                        <Route path="/vehicles" element={<Vehicles />} />
                        <Route path="/vehicles/:id" element={<VehicleDetail />} />
                        <Route path="/health-score" element={<HealthScore />} />
                        <Route path="/milestones" element={<Milestones />} />
                        <Route path="/features" element={<Features />} />
                        <Route path="/investments" element={<Investments />} />
                        <Route path="/tax-centre" element={<TaxCentre />} />
                        <Route path="/family" element={<Family />} />
                        <Route path="/journeys" element={<Journeys />} />
                        <Route path="/journeys/:id" element={<JourneyDetail />} />
                        <Route path="/smart-inbox" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE}><SmartCaptureInbox /></ProtectedRoute>} />
                        <Route path="/forex" element={<ForexDashboard />} />
                        <Route path="/forex-cards" element={<ForexCards />} />
                        <Route path="/forex-wallets" element={<ForexWallets />} />
                        <Route path="/forex-expenses" element={<ForexExpenses />} />
                        <Route path="/forex-reloads" element={<ForexReloads />} />
                        <Route path="/trips" element={<Trips />} />
                        <Route path="/organise/travel" element={<Travel />} />
                        <Route path="/organise/travel/:section" element={<Travel />} />
                        <Route path="/travel/airport-mode/:id" element={<AirportMode />} />
                        <Route path="/together/people-access" element={<PeopleAccess />} />
                        {/* /security, /health moved to the public block above.
                            Authenticated account security lives at /settings/security. */}
                        <Route path="/cards" element={<Cards />} />
                        <Route path="/cards/new" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE}><CardForm /></ProtectedRoute>} />
                        <Route path="/cards/:id/view" element={<CardDetail />} />
                        <Route path="/cards/:id" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE_VIEWER}><CardForm /></ProtectedRoute>} />
                        <Route path="/expenses" element={<Expenses />} />
                        <Route path="/quick-entry" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE_ENTRY}><QuickEntry /></ProtectedRoute>} />
                        <Route path="/statements" element={<Statements />} />
                        <Route path="/statements/:id/ledger" element={<StatementLedgerPage />} />
                        <Route path="/payments" element={<Payments />} />
                        <Route path="/reconciliation" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE}><Reconciliation /></ProtectedRoute>} />
                        <Route path="/bank-accounts" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE}><BankAccounts /></ProtectedRoute>} />
                        <Route path="/subscriptions" element={<Subscriptions />} />
                        <Route path="/disputes" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE}><Disputes /></ProtectedRoute>} />
                        <Route path="/calendar-classic" element={<CalendarView />} />
                        <Route path="/forward-view" element={<ForwardView />} />
                        <Route path="/insights/data-freshness" element={<DataFreshness />} />
                        <Route path="/goals" element={<Goals />} />
                        <Route path="/settings/connected-services" element={<ConnectedServices />} />
                        <Route path="/admin/integrations/market-approval" element={<MarketApproval />} />
                        <Route path="/admin/content/testimonials" element={<AdminTestimonials />} />
                        <Route path="/admin/brand/social-identity" element={<BrandSocialIdentity />} />
                        <Route path="/admin/brand/creative-qa" element={<CreativeQA />} />
                        <Route path="/admin/growth/competitor-intel" element={<CompetitorIntel />} />
                        <Route path="/admin/growth/launch-readiness" element={<LaunchReadiness />} />
                        <Route path="/admin/ai/claude" element={<ClaudeSettings />} />
                        <Route path="/household" element={<VaultTogether />} />
                        <Route path="/household/goals" element={<HouseholdGoals />} />
                        <Route path="/household/tasks" element={<HouseholdTasks />} />
                        <Route path="/household/reviews" element={<HouseholdReviews />} />
                        <Route path="/household/checkin" element={<CoupleCheckIn />} />
                        <Route path="/household/family-review" element={<FamilyReview />} />
                        <Route path="/household/summary" element={<HouseholdSummary />} />
                        <Route path="/household/onboarding" element={<HouseholdOnboarding />} />
                        <Route path="/household/money-map" element={<HouseholdMoneyMap />} />
                        <Route path="/reports" element={<Reports />} />
                        <Route path="/users" element={<ProtectedRoute roles={ROLES_ADMIN_ONLY}><Users /></ProtectedRoute>} />
                        <Route path="/settings" element={<ProtectedRoute roles={ROLES_ADMIN_ONLY}><AdminSettings /></ProtectedRoute>} />
                        <Route path="/card-history" element={<CardHistory />} />
                        <Route path="/card-holders" element={<CardHolders />} />
                        <Route path="/budgets" element={<Budgets />} />
                        <Route path="/offers" element={<Offers />} />
                        <Route path="/alerts" element={<Alerts />} />
                        <Route path="/documents" element={<Documents />} />
                        <Route path="/monthly-closing" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE}><MonthlyClosing /></ProtectedRoute>} />
                        <Route path="/audit-trail" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE}><AuditTrail /></ProtectedRoute>} />
                        <Route path="/income" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE_VIEWER}><Income /></ProtectedRoute>} />
                        <Route path="/cash-flow" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE_VIEWER}><CashFlow /></ProtectedRoute>} />
                        <Route path="/exceptions" element={<ExceptionCentre />} />
                        <Route path="/bills" element={<Bills />} />
                        <Route path="/bills/new" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE}><BillForm /></ProtectedRoute>} />
                        <Route path="/bills/:id" element={<BillDetail />} />
                        <Route path="/bills/:id/edit" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE}><BillForm /></ProtectedRoute>} />
                        <Route path="/statement-passwords" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE}><StatementPasswords /></ProtectedRoute>} />
                        <Route path="/annual-fee-waiver" element={<AnnualFeeWaiver />} />
                        <Route path="/card-advisor" element={<CardAdvisor />} />
                        <Route path="/refunds" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE_VIEWER}><Refunds /></ProtectedRoute>} />
                        <Route path="/auditor-shares" element={<ProtectedRoute roles={["admin","finance"]}><AuditorShares /></ProtectedRoute>} />
                        <Route path="/setup" element={<SetupWizard />} />
                        <Route path="/statements/review" element={<ProtectedRoute roles={ROLES_ADMIN_FINANCE}><StatementReview /></ProtectedRoute>} />
                        <Route path="/data-health" element={<DataHealth />} />
                        <Route path="/ai" element={<ProtectedRoute><VaultIntelligence /></ProtectedRoute>} />
                        <Route path="/ai/history" element={<ProtectedRoute><VaultIntelligence /></ProtectedRoute>} />
                        <Route path="/super-admin" element={<ProtectedRoute roles={["admin"]}><SuperAdmin /></ProtectedRoute>} />
                        <Route path="/super-admin/command-centre" element={<ProtectedRoute roles={["admin"]}><SuperAdminCommandCentre /></ProtectedRoute>} />
                        <Route path="/super-admin/customer-success" element={<ProtectedRoute roles={["admin"]}><SuperAdminCustomerSuccess /></ProtectedRoute>} />
                        <Route path="/super-admin/launch-centre" element={<ProtectedRoute roles={["admin"]}><SuperAdminLaunchCentre /></ProtectedRoute>} />
                        <Route path="/super-admin/releases" element={<ProtectedRoute roles={["admin"]}><SuperAdminReleases /></ProtectedRoute>} />
                        <Route path="/saas-ops" element={<ProtectedRoute roles={["admin"]}><SaasOps /></ProtectedRoute>} />
                        <Route path="/legal-config" element={<ProtectedRoute roles={["admin"]}><LegalConfigCentre /></ProtectedRoute>} />
                        <Route path="/cash-desk" element={<CashDesk />} />
                        <Route path="/settings/security" element={<SettingsScreenPrivacy />} />
                        <Route path="/settings/privacy" element={<SettingsScreenPrivacy />} />
                        <Route path="/settings/data-export" element={<SettingsScreenDataExport />} />
                        <Route path="/settings/merchant-rules" element={<MerchantRulesAdmin />} />
                        <Route path="/compliance-ops" element={<ProtectedRoute roles={["admin"]}><ComplianceOps /></ProtectedRoute>} />
                        <Route path="/compliance-cockpit" element={<ProtectedRoute roles={["admin"]}><ComplianceCockpit /></ProtectedRoute>} />
                        <Route path="/admin/contact-messages" element={<ProtectedRoute roles={["admin"]}><ContactMessagesAdmin /></ProtectedRoute>} />
                        <Route path="/admin/paddle-readiness" element={<ProtectedRoute roles={["admin"]}><PaddleReadiness /></ProtectedRoute>} />
                        <Route path="/admin/support-access" element={<ProtectedRoute roles={["admin"]}><SupportAccessAdmin /></ProtectedRoute>} />
                        <Route path="/admin/feature-health" element={<ProtectedRoute roles={["admin"]}><FeatureHealth /></ProtectedRoute>} />
                        <Route path="/admin/features/matrix" element={<ProtectedRoute roles={["admin"]}><FeatureHealth /></ProtectedRoute>} />
                        <Route path="/admin/marketing" element={<ProtectedRoute roles={["admin"]}><MarketingCentre /></ProtectedRoute>} />
                        <Route path="/admin/growth/marketing" element={<ProtectedRoute roles={["admin"]}><MarketingCentre /></ProtectedRoute>} />
                        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                        <Route path="/import/bulk" element={<ProtectedRoute><BulkImport /></ProtectedRoute>} />
                        <Route path="/settings/delete-account" element={<ProtectedRoute><DeleteAccount /></ProtectedRoute>} />
                        <Route path="/cards/intelligence" element={<ProtectedRoute><CardIntelligence /></ProtectedRoute>} />
                        <Route path="/action-centre" element={<ProtectedRoute><ActionCentre /></ProtectedRoute>} />
                        <Route path="/settings/wallet" element={<ProtectedRoute><WalletSettings /></ProtectedRoute>} />
                        <Route path="/security/passkeys" element={<PasskeysManager />} />
                        <Route path="/admin/authentication" element={<ProtectedRoute roles={["admin"]}><AuthenticationMetrics /></ProtectedRoute>} />
                        <Route path="/admin/system-health" element={<ProtectedRoute roles={["admin"]}><SystemHealth /></ProtectedRoute>} />
                        <Route path="/forecast" element={<ProtectedRoute><Forecast /></ProtectedRoute>} />
                        <Route path="/review-queue" element={<ProtectedRoute><ReviewQueue /></ProtectedRoute>} />
                        <Route path="/organise/vault-inbox" element={<ProtectedRoute><OrganiseVaultInbox /></ProtectedRoute>} />
                        <Route path="/profile/journey" element={<ProtectedRoute><VaultJourney /></ProtectedRoute>} />
                        <Route path="/reports/cash-flow" element={<CashFlowReport />} />
                        <Route path="/settings/connected-accounts" element={<ConnectedAccounts />} />
                        <Route path="/admin/integrations/providers" element={<ProtectedRoute roles={["admin"]}><IntegrationsAdmin /></ProtectedRoute>} />
                        <Route path="/privacy-data" element={<PrivacyDataDashboard />} />
                        <Route path="/checkout/confirm" element={<CheckoutConfirm />} />
                        <Route path="/checkout/success" element={<CheckoutConfirm />} />
                        <Route path="/settings/billing" element={<BillingPlan />} />
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Route>
                </Routes>
            </BrowserRouter>
            <Toaster position="top-right" richColors />
            <StepUpModal />
            <ScreenshotToast />
            <CookieConsent />
            </LocaleProvider>
            </PrefsProvider>
        </AuthProvider>
        </FeatureStatusProvider>
        </HelmetProvider>
        </RootErrorBoundary>
    );
}
